import { DiContainer } from '@/modules/di/di-container';
import { BuyersService } from '../services/buyers.service';
import { createEffect, createEvent, createStore, sample } from 'effector';
import persist from 'effector-localstorage';
import {
  BuyerInstance,
  BuyerInstanceToUpdate,
  IReceivingPoint,
  IReceivingPointCreate,
} from '../ts';
import { ACTIVE_BUYER, ACTIVE_BUYER_TYPE } from '../constants';

const buyersService = DiContainer.get(BuyersService);

// GET ALL BUYERS LIST

export const getBuyers = createEvent<any>();
const setBuyersLoadingStatus = createEvent<{ loading: boolean; error: boolean }>();

export const setLocalDisabledBuyer = createEvent<number>();
export const $localDisabledBuyers = createStore<number[]>([]).on(
  setLocalDisabledBuyer,
  (s, p) => [...s, p],
);

export const $buyersDataLoading = createStore({ loading: false, error: false });
export const $buyersData = createStore<any>(null).on(getBuyers, (_, newData) => {
  return newData;
});

export const fetchBuyersListFx = createEffect(
  async ({
    sortBy = 'company_name',
    orderBy,
    search,
    invisible,
  }: {
    sortBy?: string;
    orderBy?: 'asc' | 'desc';
    search: string;
    invisible?: boolean;
  }) => {
    try {
      !invisible && setBuyersLoadingStatus({ loading: true, error: false });
      let data = await buyersService.getAllBuyers({
        sort_by: sortBy,
        order_by: orderBy,
        search: search || undefined,
      });
      !invisible && setBuyersLoadingStatus({ loading: false, error: false });
      $localDisabledBuyers.reset();
      return data;
    } catch (err: any) {
      !invisible && setBuyersLoadingStatus({ loading: false, error: true });
    }
  },
);

sample({
  clock: fetchBuyersListFx.doneData,
  target: getBuyers,
});

// GET BUYER BY ID
export const getBuyerById = createEvent<any>();
export const setBuyerById = createEvent<any>();

// Создаем хранилище с начальным значением null
export const $singleBuyerData = createStore<BuyerInstance | null>(null);

// Настраиваем обработчики событий
$singleBuyerData
  .on(getBuyerById, (_, newData) => {
    localStorage.setItem(ACTIVE_BUYER, String(newData.id));
    localStorage.setItem(
      ACTIVE_BUYER_TYPE,
      JSON.stringify({
        type: newData.type,
        company_name: newData.company_name,
      }),
    );
    return newData;
  })
  .on(setBuyerById, (_s, p) => {
    return p;
  });

// Настраиваем сохранение в localStorage
persist({
  store: $singleBuyerData,
  key: 'buyer_data',
  def: null, // Значение по умолчанию при отсутствии данных
  sync: true, // Синхронизация между вкладками браузера
});

export const fetchBuyerByIdFx = createEffect(
  async (args: { id: number; md5?: string; invisible?: boolean }) => {
    try {
      //console.log(args)
      if (args.md5 !== undefined) {
        let data = await buyersService.getBuyerByMd5(args.md5 as string);
        return data;
      }
      let data = await buyersService.getBuyerById(args.id);
      return data;
    } catch (err) {
      return err;
    }
  },
);

sample({
  clock: fetchBuyerByIdFx.doneData,
  target: getBuyerById,
});

// CREATE BUYER
const createBuyer = createEvent<any>();
export const $newBuyerResponse = createStore<any>(null).on(
  createBuyer,
  (_, newData) => {
    return newData;
  },
);

export const createNewBuyerFx = createEffect(async (buyerDto: BuyerInstance) => {
  try {
    let data = await buyersService.createNewBuyer(buyerDto);
    return data;
  } catch (err) {
    return { error: err };
  }
});

// sample({
//   clock: createNewBuyerFx.doneData,
//   target: getBuyerById,
// });

// UPDATE BUYER
export const updateBuyerFx = createEffect(
  async (args: { id: number; md5?: string; data: BuyerInstanceToUpdate }) => {
    try {
      let data: any = await buyersService.updateBuyerById(args.id, args.data, args.md5);
      return data;
    } catch (err: any) {
      if (err?.status === 422) {
        const res = await err.json();
        return res;
      }
      return { error: err.message };
    }
  },
);

// DELETE BUYER
export const deleteBuyerFx = createEffect(async (id: number) => {
  try {
    let data = await buyersService.deleteBuyerById(id);
    return data;
  } catch (err: any) {
    console.log(err, '1');
    return await err.json();
  }
});

export const setActiveBuyer = createEvent<any>();

export const $activeBuyer = createStore<any>(-1).on(setActiveBuyer, (_, val) => {
  return val;
});

// SEARCH ADDRESS
export const searchBuyersAddressFx = createEffect(async (query: string) => {
  try {
    let data = await buyersService.searchBuyersAddress(query);
    return data;
  } catch (err) {
    return { error: err };
  }
});

export const setAddressOptions = createEvent<any>();

export const clearAddressOptions = () => {
  setAddressOptions([]);
};

export const $addressOptions = createStore([]).on(setAddressOptions, (_, val) => {
  return val;
});

sample({
  clock: searchBuyersAddressFx.doneData,
  target: setAddressOptions,
});

// SEARCH BUYER REQUISITES
export const searchBuyersRequisitesFx = createEffect(async (query: string) => {
  try {
    let data = await buyersService.searchBuyerInGlobalDatabase(query);
    return data;
  } catch (err) {
    return { error: err };
  }
});

export const setRequisiteOptions =
  createEvent<
    {
      name: string;
      inn: string;
      ogrn: string;
      address: { city: string; country: string; house: string; street: string };
    }[]
  >();

export const clearRequisiteOptions = () => {
  setRequisiteOptions([]);
};

export const $requisiteOptions = createStore<
  | {
      name: string;
      inn: string;
      ogrn: string;
      address: { city: string; country: string; house: string; street: string };
    }[]
  | []
>([]).on(setRequisiteOptions, (_, val) => {
  return val;
});

sample({
  clock: searchBuyersRequisitesFx.doneData,
  target: setRequisiteOptions,
});

// SEARCH BUYER POINT ADDRESS
export const setPointAddress = createEvent<IReceivingPoint>();

export const resetPointAddress = createEvent();

const pointAddressInitialState = {
  coordinates: '',
  country: '',
  region: '',
  area: '',
  street: '',
  house: '',
  latitude: '',
  longitude: '',
  enabled: 1,
  has_mixer: 0,
  name: '',
};

export const $pointAddress = createStore<IReceivingPoint>(
  pointAddressInitialState as IReceivingPoint,
)
  .on(setPointAddress, (_s, payload) => {
    return { ...payload };
  })
  .on(resetPointAddress, () => {
    return pointAddressInitialState as IReceivingPoint;
  });

// GET BUYER POINTS
const setReceivingPoints = createEvent();
export const $selectedBuyerReceivingPoints = createStore([]).on(
  setReceivingPoints,
  (_state, payload) => {
    return payload;
  },
);

const setBuyerPointsLoadingStatus = createEvent<{ loading: boolean; error: boolean }>();
export const $buyerPointsLoadingStatus = createStore({ loading: false, error: false });

export const getBuyerPointsFx = createEffect(async (id: number) => {
  try {
    setBuyerPointsLoadingStatus({ loading: true, error: false });
    const data = await buyersService.getBuyerReceivingPoints(id);
    setBuyerPointsLoadingStatus({ loading: false, error: false });
    return data;
  } catch (err) {
    setBuyerPointsLoadingStatus({ loading: false, error: true });
    return [];
  }
});

sample({
  clock: getBuyerPointsFx.doneData,
  target: setReceivingPoints,
});

// CREATE RECEIVING POINT
export const createReceivingPoint = createEffect(
  async (addressData: {
    buyer_id: number;
    region: string;
    city: string;
    street: string;
    house: string;
    name: string;
  }) => {
    try {
      const res = await buyersService.createBuyerReceivingPoints(addressData);
      return res;
    } catch (err) {
      return { error: err };
    }
  },
);

// DELETE RECEIVING POINT
export const deleteReceivingPoint = createEffect(async (id: number) => {
  try {
    const res = await buyersService.deleteReceivingPoint(id);
    return res;
  } catch (err: any) {
    const errRes = await err.json();
    return errRes || err;
  }
});

// EDIT GRAIN RECEIVING POINT
export const editGrainReceivingPoint = createEffect(
  async (
    addressData: Omit<IReceivingPoint, 'coordinates' | 'buyerable_id' | 'country'>,
  ) => {
    try {
      const res: any = await buyersService.editGrainReceivingPoint(addressData);
      if (res?.status) {
        return await res.json();
      }
      return res;
    } catch (err: any) {
      return await err.json();
    }
  },
);

// GET PRICE DYNAMICS BY BUYER
const getPriceDynamicsById = createEvent();
export const setInitialStateDynamicsById = createEvent();

export const $priceDynamicsByBuyer = createStore<any>([])
  .on(getPriceDynamicsById, (_s, payload) => {
    return payload;
  })
  .on(setInitialStateDynamicsById, () => []);

export const fetchPriceDynamixById = createEffect(async (buyerId: number) => {
  try {
    const res = await buyersService.getPriceDynamics(buyerId);
    return res;
  } catch (err: any) {
    return { error: await err.json() };
  }
});

sample({
  clock: fetchPriceDynamixById.doneData,
  target: getPriceDynamicsById,
});

// SWITCH ALL BUYERS
export const switchAllBuyersFx = createEffect(async (enabled: boolean) => {
  try {
    const res = await buyersService.switchAllBuyers({ enabled });
    return res;
  } catch (err: any) {
    return { error: await err.json() };
  }
});

export const $buyersSwitching = createStore(false)
  .on(switchAllBuyersFx.pending, () => true)
  .on(switchAllBuyersFx.doneData, () => false)
  .on(switchAllBuyersFx.failData, () => false);
